import { BACKEND_CONFIG } from '../config/backendConfig';
/**
 * Analytics API Service
 * 
 * Provides access to recruitment engagement analytics endpoints.
 * Base URL: https://api.apsrecruiting.io/api/analytics
 */

const BASE_URL = `${BACKEND_CONFIG.baseURL}/analytics`;

// TypeScript Interfaces
export type InterestLevel = 'high' | 'medium' | 'low';
export type ExportFormat = 'json' | 'csv';
export type MetricType = 'emails_sent' | 'emails_opened' | 'tweets';

/**
 * Email and social engagement from the backend's utils/engagementMetrics.js.
 * Every page that shows engagement reads this shape. Rates are percents with
 * one decimal, or null when nothing was sent or viewed.
 */
export interface EngagementMetrics {
  emailsSent: number;
  trueOpens: number;
  coachReplies: number;
  openRate: number | null;
  replyRate: number | null;
  emailEngagementRate: number | null;
  posts: { total: number; x: number; youtube: number };
  views: number;
  likes: number;
  comments: number;
  shares: number;
  avgViewsPerPost: number | null;
  socialEngagementRate: number | null;
  schoolsContacted: number;
}

export interface OverviewStats {
  totalOutreach: number;
  emailsSent: number;
  tweetInteractions: number;
  videoViews: number;
  additionalMetrics: {
    emailOpenRate: number;
    emailReplyRate: number;
    emailsOpened: number;
    emailsReplied: number;
  };
  totalEngagementMetrics: {
    totalViews: number;
    totalLikes: number;
    totalComments: number;
    totalShares: number;
    // Same number as engagement.emailEngagementRate.
    engagementRate: string;
  };
  engagement?: EngagementMetrics;
}

export interface CollegeBreakdownData {
  name: string;
  value: number;
  color: string;
}

export interface CollegeBreakdown {
  data: CollegeBreakdownData[];
  total: number;
  page: number;
  totalPages: number;
}

export interface FunnelStage {
  stage: string;
  count: number;
  percentage: number;
  color: string;
}

export interface EmailFunnel {
  funnelData: FunnelStage[];
  conversionRate: number;
}

export interface SocialTrendsData {
  month: string;
  twitterViews: number;
  youtubeViews: number;
  instagramViews: number;
  tiktokViews: number;
}

export interface SocialTrends {
  data: SocialTrendsData[];
}

export interface EmailTrendsData {
  month: string;
  sent: number;
  opened: number;
  replied: number;
}

export interface EmailTrends {
  data: EmailTrendsData[];
}

export interface CollegeEngagementRow {
  college: string;
  sport: string;
  interest: InterestLevel;
  emails: number;
  opens: number;
  openRate: string;
  replies: number;
  tweets: number;
}

export interface CollegeEngagement {
  data: CollegeEngagementRow[];
  total: number;
  page: number;
  totalPages: number;
}

export interface ExportData {
  athlete_name: string;
  graduation_year: number;
  primary_sport: string;
  gpa: number;
  sat_score: number;
  act_score: number;
  emails_sent: number;
  emails_opened: number;
  emails_received: number;
  total_posts: number;
  total_likes: number;
  total_comments: number;
  total_shares: number;
  schools_contacted: number;
  export_date: string;
  date_range: string;
}

export interface ExportResponse {
  data: ExportData;
}

/**
 * Fetch overview statistics with period-over-period comparisons
 */
export async function getOverview(token: string, athleteId?: string): Promise<OverviewStats> {
  const url = athleteId 
    ? `${BASE_URL}/overview?athleteId=${athleteId}`
    : `${BASE_URL}/overview`;
    
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to fetch overview' } }));
    throw new Error(error.error?.message || 'Failed to fetch overview');
  }

  return response.json();
}

/**
 * Fetch college engagement breakdown for pie chart
 */
export async function getCollegeBreakdown(
  token: string,
  metric: MetricType = 'emails_sent',
  athleteId?: string
): Promise<CollegeBreakdown> {
  let url = `${BASE_URL}/college-breakdown?metric=${metric}`;
  if (athleteId) {
    url += `&athleteId=${athleteId}`;
  }
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to fetch breakdown' } }));
    throw new Error(error.error?.message || 'Failed to fetch breakdown');
  }

  return response.json();
}

/**
 * Fetch email engagement funnel
 */
export async function getEmailFunnel(token: string, athleteId?: string): Promise<EmailFunnel> {
  const url = athleteId 
    ? `${BASE_URL}/email-funnel?athleteId=${athleteId}`
    : `${BASE_URL}/email-funnel`;
    
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to fetch funnel' } }));
    throw new Error(error.error?.message || 'Failed to fetch funnel');
  }

  return response.json();
}

/**
 * Fetch social media trends over 6 months
 */
export async function getSocialTrends(token: string, athleteId?: string): Promise<SocialTrends> {
  const url = athleteId 
    ? `${BASE_URL}/social-trends?athleteId=${athleteId}`
    : `${BASE_URL}/social-trends`;
    
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to fetch social trends' } }));
    throw new Error(error.error?.message || 'Failed to fetch social trends');
  }

  return response.json();
}

/**
 * Fetch email engagement trends over 6 months
 */
export async function getEmailTrends(token: string, athleteId?: string): Promise<EmailTrends> {
  const url = athleteId 
    ? `${BASE_URL}/email-trends?athleteId=${athleteId}`
    : `${BASE_URL}/email-trends`;
    
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to fetch email trends' } }));
    throw new Error(error.error?.message || 'Failed to fetch email trends');
  }

  return response.json();
}

/**
 * Fetch detailed college engagement data for table
 */
export async function getCollegeEngagement(token: string, athleteId?: string): Promise<CollegeEngagement> {
  const url = athleteId 
    ? `${BASE_URL}/college-engagement?athleteId=${athleteId}`
    : `${BASE_URL}/college-engagement`;
    
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to fetch engagement' } }));
    throw new Error(error.error?.message || 'Failed to fetch engagement');
  }

  return response.json();
}

/**
 * Email and social engagement for one athlete. Not paywalled.
 */
export async function getEngagementSummary(token: string, athleteId: string): Promise<EngagementMetrics> {
  const response = await fetch(
    `${BACKEND_CONFIG.baseURL}/engagement/summary?athleteId=${encodeURIComponent(athleteId)}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to fetch engagement' } }));
    throw new Error(error.error?.message || (typeof error.error === 'string' ? error.error : 'Failed to fetch engagement'));
  }

  const body = await response.json();
  if (!body?.engagement) throw new Error('Failed to fetch engagement');
  return body.engagement as EngagementMetrics;
}

/**
 * Export analytics data in JSON or CSV format
 */
export async function exportAnalytics(
  token: string,
  format: ExportFormat = 'json',
  dateRange: number = 90
): Promise<ExportResponse | Blob> {
  const response = await fetch(`${BASE_URL}/export?format=${format}&dateRange=${dateRange}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to export' } }));
    throw new Error(error.error?.message || 'Failed to export');
  }

  if (format === 'csv') {
    return response.blob();
  }

  return response.json();
}

/**
 * Helper function to download CSV blob
 */
export function downloadCSV(blob: Blob, filename?: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `analytics-export-${Date.now()}.csv`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export const analyticsAPI = {
  getOverview,
  getCollegeBreakdown,
  getEmailFunnel,
  getSocialTrends,
  getEmailTrends,
  getCollegeEngagement,
  getEngagementSummary,
  exportAnalytics,
  downloadCSV
};
